// src/services/sheets/SheetContextBuilder.ts
// Builds context about a sheet for the AI Copilot to understand

import type {
  Sheet,
  SheetPurpose,
  ColumnSemanticType,
  DataSourceType,
  ColumnMetadata,
  CellFormat,
} from "@/features/sheets";
import {
  evaluateFormula,
  containsLiveFormula,
} from "@/features/sheets/utils/formulaEngine";
import {
  HEADER_TYPE_RULES,
  SUMMARY_ROW_PATTERNS,
  TICKER_HEADER_PATTERNS,
  isLikelyTicker,
  MAX_PREVIEW_ROWS,
} from "@/features/sheets/constants";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Summary of resolved data for AI consumption */
export interface ResolvedDataSummary {
  /** First 20 rows with resolved values (formulas evaluated) */
  preview: Array<{
    rowIndex: number;
    values: string[];
    isHeader: boolean;
    isSummary: boolean;
  }>;
  /** Statistics for each column */
  columnStats: Array<{
    header: string;
    nonEmptyCount: number;
    numericCount: number;
    uniqueValues: number;
    sampleValues: string[];
    range?: { min: number; max: number };
  }>;
  /** Total row count */
  totalRows: number;
  /** Whether there are more rows than shown in preview */
  hasMoreRows: boolean;
}

/** Complete sheet context for AI */
export interface SheetContext {
  id: string;
  name: string;

  // Structure info
  rowCount: number;
  colCount: number;
  headers: string[];

  // Semantic understanding
  purpose?: SheetPurpose;
  inferredColumnTypes: ColumnMetadata[];
  detectedTickers: string[];
  hasLiveData: boolean;
  hasSummaryRows: boolean;

  // Data (resolved values, not raw formulas)
  resolvedData: ResolvedDataSummary;

  // Raw data for precise operations
  rawData: string[][];

  // Format info
  formats: Record<string, CellFormat>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context Building Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a complete context object from a sheet
 * @param sheet - The sheet to build context for
 * @param liveValueCache - Optional cache of live values for formula evaluation
 * @param knownTickers - Optional set of known tickers from token catalog for validation
 */
export function buildSheetContext(
  sheet: Sheet,
  liveValueCache?: Map<string, number | null>,
  knownTickers?: Set<string>
): SheetContext {
  const headers = sheet.data[0] || [];
  const inferredTypes = inferColumnTypes(sheet.data, headers, knownTickers);
  const detectedTickers = detectTickers(sheet.data, knownTickers);
  const resolvedData = resolveSheetData(sheet, liveValueCache);

  const context: SheetContext = {
    id: sheet.id,
    name: sheet.name,
    rowCount: sheet.rows,
    colCount: sheet.cols,
    headers,
    inferredColumnTypes: inferredTypes,
    detectedTickers,
    hasLiveData: detectLiveData(sheet.data),
    hasSummaryRows: detectSummaryRows(sheet.data),
    resolvedData,
    rawData: sheet.data,
    formats: sheet.formats,
  };

  // Add optional purpose if present
  if (sheet.aiMetadata?.purpose) {
    context.purpose = sheet.aiMetadata.purpose;
  }

  return context;
}

/**
 * Infer column types from headers and values
 */
function inferColumnTypes(
  data: string[][],
  headers: string[],
  knownTickers?: Set<string>
): ColumnMetadata[] {
  return headers.map((header, colIndex) => {
    const columnValues = data.slice(1).map((row) => row[colIndex] || "");

    const semanticType = inferSemanticType(header, columnValues, knownTickers);
    const dataSource = inferDataSource(columnValues);
    const liveMetric = extractLiveMetric(columnValues);

    const metadata: ColumnMetadata = {
      header,
      semanticType,
      dataSource,
    };

    // Add optional liveMetric if present
    if (liveMetric) {
      metadata.liveMetric = liveMetric;
    }

    return metadata;
  });
}

/**
 * Infer the semantic type of a column from its header and values
 */
function inferSemanticType(
  header: string,
  values: string[],
  knownTickers?: Set<string>
): ColumnSemanticType {
  // Try header pattern matching first using centralized rules
  for (const rule of HEADER_TYPE_RULES) {
    if (rule.pattern.test(header)) {
      // Check exclude pattern if present
      if (rule.excludePattern && rule.excludePattern.test(header)) {
        continue;
      }
      return rule.type as ColumnSemanticType;
    }
  }

  // Analyze values
  const hasLiveFormulas = values.some((v) => containsLiveFormula(v));
  if (hasLiveFormulas) return "live";

  const hasFormulas = values.some((v) => v.startsWith("="));
  if (hasFormulas) return "formula";

  // Check if most values look like tickers using centralized function with token catalog
  const tickerCount = values.filter((v) => isLikelyTicker(v, knownTickers)).length;
  if (tickerCount > values.length * 0.5) return "ticker";

  // Check if most values are numeric
  const numericCount = values.filter((v) => !isNaN(parseFloat(v.replace(/[$,]/g, "")))).length;
  if (numericCount > values.length * 0.7) return "currency";

  return "text";
}

/**
 * Infer the data source type from column values
 */
function inferDataSource(values: string[]): DataSourceType {
  const hasLive = values.some((v) => containsLiveFormula(v));
  if (hasLive) return "live";

  const hasFormula = values.some((v) => v.startsWith("="));
  if (hasFormula) return "formula";

  return "manual";
}

/**
 * Extract the LIVE() metric from column values if present
 */
function extractLiveMetric(values: string[]): string | undefined {
  for (const value of values) {
    if (containsLiveFormula(value)) {
      // Parse =LIVE("TICKER", "metric") to extract metric
      const match = value.match(/LIVE\s*\(\s*"[^"]+"\s*,\s*"([^"]+)"\s*\)/i);
      if (match && match[1]) {
        return match[1];
      }
    }
  }
  return undefined;
}

/**
 * Detect all tickers mentioned in the sheet
 */
function detectTickers(data: string[][], knownTickers?: Set<string>): string[] {
  const tickers = new Set<string>();

  for (const row of data) {
    for (const cell of row) {
      // Check if cell is a known ticker (use token catalog if available)
      const upper = cell.trim().toUpperCase();
      if (knownTickers?.has(upper)) {
        tickers.add(upper);
      } else if (isLikelyTicker(cell, knownTickers)) {
        // Fallback to pattern-based detection
        tickers.add(upper);
      }

      // Also check LIVE() formulas for tickers
      if (containsLiveFormula(cell)) {
        const match = cell.match(/LIVE\s*\(\s*"([^"]+)"/i);
        if (match && match[1]) {
          tickers.add(match[1].toUpperCase());
        }
      }
    }
  }

  return Array.from(tickers);
}

/**
 * Check if the sheet contains any live data formulas
 */
function detectLiveData(data: string[][]): boolean {
  for (const row of data) {
    for (const cell of row) {
      if (containsLiveFormula(cell)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Detect if the sheet has summary rows (total, average, etc.)
 */
function detectSummaryRows(data: string[][]): boolean {
  for (const row of data) {
    const firstCell = (row[0] || "").trim();
    if (SUMMARY_ROW_PATTERNS.test(firstCell)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a row is a summary row (e.g., Average, Total)
 * Exported for use by other modules
 */
export function isSummaryRow(row: string[]): boolean {
  const firstCell = (row[0] || "").trim();
  return SUMMARY_ROW_PATTERNS.test(firstCell);
}

/**
 * Find the ticker column in a sheet by analyzing headers and data
 * Exported for use by other modules
 * @param data - The sheet data
 * @param knownTickers - Optional set of known tickers from token catalog
 */
export function findTickerColumn(data: string[][], knownTickers?: Set<string>): number {
  const headers = data[0] || [];

  // First, check headers for ticker-like names
  const tickerHeaderIdx = headers.findIndex((h) =>
    TICKER_HEADER_PATTERNS.test((h || "").trim())
  );
  if (tickerHeaderIdx >= 0) return tickerHeaderIdx;

  // Otherwise, find column with most ticker-like values
  let bestCol = 0;
  let bestCount = 0;

  for (let col = 0; col < headers.length; col++) {
    let tickerCount = 0;
    // Check all data rows (not just first 10)
    for (let row = 1; row < data.length; row++) {
      const cell = data[row]?.[col]?.trim() || "";
      if (isLikelyTicker(cell, knownTickers)) {
        tickerCount++;
      }
    }
    if (tickerCount > bestCount) {
      bestCount = tickerCount;
      bestCol = col;
    }
  }

  return bestCol;
}

/**
 * Resolve sheet data by evaluating formulas
 */
function resolveSheetData(
  sheet: Sheet,
  liveCache?: Map<string, number | null>
): ResolvedDataSummary {
  // Build cell getter for formula evaluation
  const getCell = (row: number, col: number): string => {
    return sheet.data[row]?.[col] || "";
  };

  // Resolve first MAX_PREVIEW_ROWS rows
  const preview = sheet.data.slice(0, MAX_PREVIEW_ROWS).map((row, rowIndex) => ({
    rowIndex,
    values: row.map((cell) => {
      if (cell.startsWith("=")) {
        try {
          const result = evaluateFormula(cell, getCell, new Set(), liveCache);
          if (result.isLoading) return "...";
          if (result.error) return `#${result.error}`;
          return String(result.value);
        } catch {
          return "#ERROR";
        }
      }
      return cell;
    }),
    isHeader: rowIndex === 0,
    isSummary: isSummaryRow(row),
  }));

  // Calculate column statistics
  const columnStats = (sheet.data[0] || []).map((header, colIndex) => {
    const values = sheet.data.slice(1).map((row) => row[colIndex] || "");
    const numericValues = values
      .map((v) => parseFloat(v.replace(/[$,]/g, "")))
      .filter((n) => !isNaN(n));

    const stat: {
      header: string;
      nonEmptyCount: number;
      numericCount: number;
      uniqueValues: number;
      sampleValues: string[];
      range?: { min: number; max: number };
    } = {
      header,
      nonEmptyCount: values.filter((v) => v.trim()).length,
      numericCount: numericValues.length,
      uniqueValues: new Set(values).size,
      sampleValues: [...new Set(values)].slice(0, 5),
    };

    // Add optional range if we have numeric values
    if (numericValues.length > 0) {
      stat.range = { min: Math.min(...numericValues), max: Math.max(...numericValues) };
    }

    return stat;
  });

  return {
    preview,
    columnStats,
    totalRows: sheet.data.length,
    hasMoreRows: sheet.data.length > MAX_PREVIEW_ROWS,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Serialization for AI Prompt
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serialize sheet context into a format suitable for the AI prompt
 */
export function serializeContextForPrompt(context: SheetContext): string {
  const sections: string[] = [];

  // Sheet header
  sections.push(`## Current Sheet: "${context.name}"`);
  sections.push("");

  // Structure info
  sections.push("### Sheet Structure");
  sections.push(`- Total: ${context.rowCount} rows × ${context.colCount} columns`);
  sections.push("");
  sections.push("**Column Headers:**");
  sections.push(`${context.headers.map((h, i) => `  ${i}. ${h}`).join("\n")}`);
  sections.push("");
  if (context.detectedTickers.length > 0) {
    sections.push(`**Detected Tickers:** ${context.detectedTickers.join(", ")}`);
  }
  sections.push("");
  sections.push(`- Has Live Data: ${context.hasLiveData ? "Yes" : "No"}`);
  sections.push(`- Has Summary Row: ${context.hasSummaryRows ? "Yes" : "No"}`);
  if (context.purpose) {
    sections.push(`- Purpose: ${context.purpose}`);
  }
  sections.push("");

  // Column analysis
  sections.push("### Column Analysis");
  for (const col of context.inferredColumnTypes) {
    let desc = `- **${col.header}**: ${col.semanticType} (${col.dataSource})`;
    if (col.liveMetric) {
      desc += ` [LIVE: ${col.liveMetric}]`;
    }
    sections.push(desc);
  }
  sections.push("");

  // Data preview
  sections.push("### Data Preview");
  sections.push("```");
  for (const row of context.resolvedData.preview) {
    let prefix = "";
    if (row.isHeader) {
      prefix = "[Header] ";
    } else if (row.isSummary) {
      prefix = "[Summary] ";
    } else {
      prefix = `[Row ${row.rowIndex}] `;
    }
    sections.push(prefix + row.values.join(" | "));
  }
  sections.push("```");

  if (context.resolvedData.hasMoreRows) {
    sections.push(
      `... and ${context.resolvedData.totalRows - 20} more rows`
    );
  }
  sections.push("");

  // Column statistics
  sections.push("### Column Statistics");
  for (const stat of context.resolvedData.columnStats) {
    let desc = `- **${stat.header}**: ${stat.nonEmptyCount} values`;
    if (stat.numericCount > 0) {
      desc += `, ${stat.numericCount} numeric`;
    }
    if (stat.range) {
      desc += `, range: ${formatNumber(stat.range.min)} - ${formatNumber(stat.range.max)}`;
    }
    sections.push(desc);
  }

  return sections.join("\n");
}

/**
 * Format a number for display
 */
function formatNumber(num: number): string {
  if (Math.abs(num) >= 1e9) {
    return (num / 1e9).toFixed(2) + "B";
  }
  if (Math.abs(num) >= 1e6) {
    return (num / 1e6).toFixed(2) + "M";
  }
  if (Math.abs(num) >= 1e3) {
    return (num / 1e3).toFixed(2) + "K";
  }
  return num.toFixed(2);
}

/**
 * Create a minimal context for quick operations
 */
export function buildMinimalContext(sheet: Sheet): {
  id: string;
  name: string;
  headers: string[];
  rowCount: number;
  colCount: number;
} {
  return {
    id: sheet.id,
    name: sheet.name,
    headers: sheet.data[0] || [],
    rowCount: sheet.data.length,
    colCount: sheet.data[0]?.length || 0,
  };
}
